import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { apiClient } from '../api/apiClient';
import { RoutePaths } from '../constants';
import { RootState } from '../store';
import { usersActions } from '../store/users/slice';
import { asyncUsersActions } from '../store/users/thunks';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { fetchAuthSession } from 'aws-amplify/auth';
import { appActions } from 'store/app/slice';
import { dashboardActions } from 'store/dashboard/slice';
import { asyncDashboardActions } from 'store/dashboard/thunks';
import { patientsActions } from 'store/patients/slice';
import { asyncPatientsActions } from 'store/patients/thunks';
import { SseEvent, TreatmentPlanTask, UserRole } from 'types';
import { history } from 'utils';

let sessionId = '';

const API_PATH = process.env.REACT_APP_API_URL + '/ns/api/v1/sse';

const closeConnection = () => apiClient.apiInstance.post(API_PATH + '/close', { sessionId });

const matchCurrentPath = (path: RoutePaths) => window.location.href.includes(path);

const initSseService = async (thunkAPI: GetThunkAPI<any>) => {
  const token = (await fetchAuthSession()).tokens?.accessToken.toString();

  if (!token) return;

  fetchEventSource(API_PATH, {
    headers: {
      Authorization: `Bearer ${token}`,
      accept: 'text/event-stream',
    },
    onopen: async (response) => {
      if (response.ok) {
        thunkAPI.dispatch(appActions.setSSEConnected(true));
      }
    },
    openWhenHidden: false,
    onerror: (err) => {
      closeConnection();
      console.log('sse connection error ', err);
    },
    onmessage: (event) => {
      const res = JSON.parse(event.data);
      const type: SseEvent = res.type;
      const data = res.data;
      const state = thunkAPI.getState() as RootState;

      const currentPatientId = state.patients.currentPatientCase?.id;
      const currentUserId = state.auth.currentUser?.id;

      const canPageBeUpdated = state.patients.shouldPatientPageUpdate;
      const canMessagesPageBeUpdated = state.patients.shouldMessagesPageUpdate;

      if (type.startsWith('PATIENT_CASE')) {
        const patientCaseId: number = data.id;

        if (
          type === 'PATIENT_CASE_DISMISSED' &&
          data.initiatorId === currentUserId &&
          matchCurrentPath(RoutePaths.DASHBOARD)
        ) {
          if (data.task === 'IN_TREATMENT') {
            thunkAPI.dispatch(dashboardActions.resetDashboardCasesBeforeUpdate(['InTreatment']));
            thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardInTreatment());
          } else if (data.task === 'FINISHED') {
            thunkAPI.dispatch(dashboardActions.resetDashboardCasesBeforeUpdate(['Finished']));
            thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardFinished());
          }
          thunkAPI.dispatch(dashboardActions.resetDashboardCasesBeforeUpdate(['OutOfTreatment']));
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardOutOfTreatment());
          return;
        }

        if (currentPatientId === patientCaseId) {
          if (canPageBeUpdated) {
            thunkAPI.dispatch(asyncPatientsActions.getPatientCase(patientCaseId));
          }
        }

        if (type === 'PATIENT_CASE_TASK_UPDATED') {
          if (matchCurrentPath(RoutePaths.DASHBOARD)) {
            // update message feed
            thunkAPI.dispatch(asyncDashboardActions.getFeedMessagesAmount());
            thunkAPI.dispatch(asyncDashboardActions.getFeed(state.dashboard.activeFeedTab));
          }
        }

        // update activity feed
        // any page
        if (type === 'PATIENT_CASE_ACTIVITY_FEED_EVENT') {
          thunkAPI.dispatch(patientsActions.resetActivityFeedBeforeUpdate());
          thunkAPI.dispatch(patientsActions.getActivityFeed());
          return;
        }

        if (type === 'PATIENT_CASE_CREATED') {
          // update patient cases from start and cases counter
          thunkAPI.dispatch(asyncPatientsActions.getPatientCaseCount());

          if (matchCurrentPath(RoutePaths.PATIENTS)) {
            thunkAPI.dispatch(patientsActions.resetPatientCasesBeforeUpdate());
            thunkAPI.dispatch(asyncPatientsActions.getPatientCases());
          }

          // // Show notifications
          // const sender = state.users.users.find((user) => user.id === data.initiatorId)?.name || 'Studio name';

          // if (Notification.permission === 'granted') {
          //   const notification = new Notification(sender + ' has created a new patient case', {
          //     // icon: '/icon.png',
          //   });
          //   const patientId = data.id;
          //   notification.onclick = () => {
          //     // Handle the click event, e.g., open a specific page
          //     window.open(
          //       `https://web${process.env.ENVIRONMENT === 'development' ? '-dev' : ''}.urbansmile.app/patients/` +
          //         patientId,
          //     );
          //     // Open a new tab with the desired URL
          //     // window.location.href = `https://web${process.env.ENVIRONMENT === 'development' ? '-dev' : ''}.urbansmile.app/patients/${patientId}`;
          //   };
          // }

          // don't show changes on activity feed modal/dropdown
          // don't show changes dashboard
          return;
        }

        if (type === 'PATIENT_CASE_DELETED') {
          //   // update patient cases from start and cases counter
          thunkAPI.dispatch(asyncPatientsActions.getPatientCaseCount());
          if (matchCurrentPath(RoutePaths.PATIENTS)) {
            if (currentPatientId === patientCaseId) {
              // navigate to existing patient
              thunkAPI.dispatch(patientsActions.setCurrenPatientCase(null));
              thunkAPI.dispatch(appActions.setShowPatientCaseList(true));
              history.navigate && history.navigate('/patients/');
              thunkAPI.dispatch(patientsActions.resetPatientCasesBeforeUpdate());
              thunkAPI.dispatch(asyncPatientsActions.getPatientCases());
            } else if (state.patients.patientPageFilters.isArchived) {
              // get new patients only if archived selected
              thunkAPI.dispatch(patientsActions.resetPatientCasesBeforeUpdate());
              thunkAPI.dispatch(asyncPatientsActions.getPatientCases());
            }
          }
          thunkAPI.dispatch(patientsActions.resetActivityFeedBeforeUpdate());
          thunkAPI.dispatch(patientsActions.getActivityFeed());
          return;
        }

        if (data?.task === TreatmentPlanTask.DIAGNOSIS_TO_DO) {
          // update patient cases from start
          // page = patients
          if (matchCurrentPath(RoutePaths.PATIENTS)) {
            thunkAPI.dispatch(patientsActions.resetPatientCasesBeforeUpdate());
            thunkAPI.dispatch(asyncPatientsActions.getPatientCases());
          }

          // update only new cases on dashboard
          // page dashboard
          if (matchCurrentPath(RoutePaths.DASHBOARD)) {
            thunkAPI.dispatch(dashboardActions.resetDashboardCasesBeforeUpdate(['New']));
            thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardNew());
          }
          // don't show changes on other dashboard tabs
          return;
        }

        // update patient cases from start
        // page patients
        if (matchCurrentPath(RoutePaths.PATIENTS)) {
          thunkAPI.dispatch(patientsActions.resetPatientCasesBeforeUpdate());
          thunkAPI.dispatch(asyncPatientsActions.getPatientCases());
        }

        // update dashboard cases from start
        // page dashboard
        if (matchCurrentPath(RoutePaths.DASHBOARD)) {
          thunkAPI.dispatch(
            dashboardActions.resetDashboardCasesBeforeUpdate([
              'New',
              'Letter',
              'Finished',
              'InTreatment',
              'OutOfTreatment',
            ]),
          );

          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardNew());
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardLetter());
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardInTreatment());
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardFinished());
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardOutOfTreatment());
        }

        return;
      }

      if (type.startsWith('MESSAGE')) {
        const patientCaseId: number = data.patientCaseId;

        //update page on message reacted event only if currentPatientId === patientCaseId
        if (type === 'MESSAGE_REACTED' && currentPatientId === patientCaseId && matchCurrentPath(RoutePaths.PATIENTS)) {
          thunkAPI.dispatch(patientsActions.setShouldMessagesPageUpdate(false));
          thunkAPI.dispatch(patientsActions.changeReactedMessagesAfterReactionsWithoutRequest(Number(data.id)));
          return;
        }

        //update page on message read event only if currentPatientId === patientCaseId
        if (
          type === 'MESSAGE_READ' &&
          currentPatientId === patientCaseId &&
          matchCurrentPath(RoutePaths.PATIENTS) &&
          canMessagesPageBeUpdated
        ) {
          thunkAPI.dispatch(patientsActions.resetPatientCasesBeforeUpdate());
          thunkAPI.dispatch(patientsActions.getPatientCases());
          thunkAPI.dispatch(patientsActions.setPatientMessageFilters({}));
          return;
        }

        if (currentPatientId === patientCaseId) {
          // page patients
          if (matchCurrentPath(RoutePaths.PATIENTS) && canMessagesPageBeUpdated) {
            thunkAPI.dispatch(patientsActions.setPatientMessageFilters({}));
            // return;
          }
        }

        // const sender = state.users.users.find((user) => user.id === data.initiatorId)?.name || 'Somebody';

        // if (Notification.permission === 'granted') {
        //   const notification = new Notification(sender + ' has sent you a discussion message', {
        //     // icon: '/icon.png',
        //   });
        //   const patientId = data.id;
        //   notification.onclick = () => {
        //     // Handle the click event, e.g., open a specific page
        //     window.open(
        //       `https://web${process.env.ENVIRONMENT === 'development' ? '-dev' : ''}.urbansmile.app/patients/` +
        //         patientId,
        //     );
        //   };
        // }

        // patients list
        if (matchCurrentPath(RoutePaths.PATIENTS)) {
          thunkAPI.dispatch(patientsActions.resetPatientCasesBeforeUpdate());
          thunkAPI.dispatch(patientsActions.getPatientCases());
        }

        // page dashboard
        if (matchCurrentPath(RoutePaths.DASHBOARD)) {
          thunkAPI.dispatch(asyncDashboardActions.getFeedMessagesAmount());
          thunkAPI.dispatch(asyncDashboardActions.getFeed(state.dashboard.activeFeedTab));

          // update dashboard cases (all depend on  new messages)
          thunkAPI.dispatch(
            dashboardActions.resetDashboardCasesBeforeUpdate([
              'New',
              'Letter',
              'Finished',
              'InTreatment',
              'OutOfTreatment',
            ]),
          );
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardNew());
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardLetter());
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardInTreatment());
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardFinished());
          thunkAPI.dispatch(asyncDashboardActions.getPatientCasesDashboardOutOfTreatment());
        }

        return;
      }

      if (type === 'USER_UPDATED' || type === 'USER_DELETED' || type === 'USER_CREATED') {
        thunkAPI.dispatch(asyncUsersActions.getUsers());
        return;
      }

      if (type === 'SUBSCRIBED_SUCCESSFULLY') {
        sessionId = data.sessionId;
        return;
      }

      if (type === 'USER_ONLINE') {
        thunkAPI.dispatch(usersActions.addOnlineUsersIds(data.userIds));
        return;
      }

      if (type === 'USER_OFFLINE') {
        thunkAPI.dispatch(usersActions.removeOnlineUserId(data.userIds[0]));
        return;
      }
    },
    onclose: async () => {
      const token = (await fetchAuthSession()).tokens?.accessToken.toString();

      if (document.visibilityState === 'visible' && token) {
        initSseService(thunkAPI);
      } else {
        console.log('sse connection closed ');
      }
    },
  });
};

export const sseService = { initSseService, closeConnection };
